import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Nav } from "../components/site/Nav";
import { Footer } from "../components/site/Footer";
import { Section, Eyebrow } from "../components/site/Primitives";
import { FadeUp, MaskedLines, EASE } from "../components/site/Reveal";

/**
 * Loop Lab — folder stack.
 *
 * Layout matches the reference image directly:
 *   • Folder bodies stack FLUSH — bottom of one touches the top of the next,
 *     no gap between them.
 *   • Each folder's TAB pokes up above its own body, sitting inside the
 *     folder above's body region. Tabs are staggered on the X-axis so all
 *     three tab labels remain visible simultaneously.
 *   • Lower folders have higher z-index so their tabs render on top of the
 *     folder above's body.
 *
 * Interaction is a three-state reveal:
 *   1. Collapsed  — default. Just the name + slim body strip.
 *   2. Peek       — on hover. Folder tilts + lifts, height grows to preview
 *                   the description copy. Reads like a folder being pulled
 *                   halfway out of a drawer.
 *   3. Expanded   — on click. Folder settles flat, grows to full height, and
 *                   reveals the stage indicator + metadata. Click again (or
 *                   another folder) to close.
 */

/* ────────────────────── Data ────────────────────── */

const LAB_PROJECTS = [
  {
    idx: "01",
    status: "LIVE",
    name: "Lurk App",
    line: "What if your morning scroll was actually a competitive advantage? What if the post you saved at midnight was already a brief?",
    stage: 3,
    stageLabels: ["Building", "Testing", "Running"],
    meta: "v1.2",
    category: "Social media intelligence",
    body: "#F0D97C",
    tab: "#E4C852",
    text: "#1A2333",
    softText: "rgba(26,35,51,0.62)",
    subtleText: "rgba(26,35,51,0.44)",
    hairline: "rgba(26,35,51,0.24)",
    accent: "#1A2333",
  },
  {
    idx: "02",
    status: "TESTING",
    name: "InboxLoop",
    line: "An inbox that closes itself. Invoices, POs and vendor emails auto-mapped to your ledger, one confirm away.",
    stage: 2,
    stageLabels: ["Building", "Testing", "Running"],
    meta: "beta",
    category: "Ops automation",
    body: "#7C8598",
    tab: "#68727F",
    text: "#F6F3EE",
    softText: "rgba(246,243,238,0.72)",
    subtleText: "rgba(246,243,238,0.52)",
    hairline: "rgba(246,243,238,0.28)",
    accent: "#F7D048",
  },
  {
    idx: "03",
    status: "ARCHIVED",
    name: "MeetingMinder",
    line: "Live meeting outcomes and action-owner routing. Sharp idea, weak signal. Shelved for now.",
    stage: 3,
    stageLabels: ["Building", "Testing", "Running"],
    meta: "shelved",
    category: "Meetings",
    body: "#D6CFBE",
    tab: "#BEB6A0",
    text: "#1A2333",
    softText: "rgba(26,35,51,0.55)",
    subtleText: "rgba(26,35,51,0.40)",
    hairline: "rgba(26,35,51,0.22)",
    accent: "#1A2333",
  },
];

const BAND_W = 980;
const COLLAPSED_H = 88;
const PEEK_H = 200;
const EXPANDED_H = 380;
const TAB_W = 240;
const TAB_H = 44;

// Staggered X positions for tabs — spread across the width so all three
// stay visible when the folders are collapsed and stacked.
const TAB_OFFSETS = [56, 360, 664];

const FIBER =
  "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.28 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

// Trapezoidal folder tab shape — narrower at top, wider at bottom.
const TAB_CLIP = "polygon(6% 0, 94% 0, 100% 100%, 0 100%)";

/* ────────────────────── Stage indicator ────────────────────── */

function StageIndicator({ p }) {
  const { stage, stageLabels, text, hairline, accent, softText } = p;
  const nodes = stageLabels.length;
  const dim = p.status === "ARCHIVED";

  return (
    <div className="w-[260px]">
      <div className="relative h-3">
        <div
          className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2"
          style={{ background: hairline }}
        />
        {!dim && (
          <div
            className="absolute left-0 top-1/2 h-px -translate-y-1/2"
            style={{
              width: `${((stage - 1) / (nodes - 1)) * 100}%`,
              background: text,
              opacity: 0.5,
            }}
          />
        )}
        {Array.from({ length: nodes }).map((_, i) => {
          const isActive = i === stage - 1;
          const isPast = i < stage - 1;
          const left = `${(i / (nodes - 1)) * 100}%`;
          return (
            <span
              key={i}
              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ left }}
            >
              {isActive && !dim && (
                <motion.span
                  aria-hidden="true"
                  className="absolute left-1/2 top-1/2 rounded-full"
                  style={{
                    width: 22,
                    height: 22,
                    marginLeft: -11,
                    marginTop: -11,
                    background: accent,
                  }}
                  animate={{ scale: [1, 2.2, 1], opacity: [0.55, 0, 0.55] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
                />
              )}
              <span
                className="relative block rounded-full border"
                style={{
                  width: isActive ? 10 : 7,
                  height: isActive ? 10 : 7,
                  background: isActive && !dim ? accent : isPast && !dim ? text : "transparent",
                  borderColor: isActive && !dim ? accent : hairline,
                }}
              />
            </span>
          );
        })}
      </div>

      <div className="relative mt-3 h-4">
        {stageLabels.map((lbl, i) => {
          const isActive = i === stage - 1;
          return (
            <span
              key={i}
              className="absolute -translate-x-1/2 font-mono-c text-[0.58rem] uppercase tracking-[0.16em] whitespace-nowrap"
              style={{
                left: `${(i / (nodes - 1)) * 100}%`,
                color: isActive && !dim ? accent : softText,
                fontWeight: isActive ? 600 : 400,
                opacity: isActive ? 1 : 0.7,
              }}
            >
              {lbl}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/* ────────────────────── Folder ────────────────────── */

function Folder({ p, i, mode, onEnter, onLeave, onClick }) {
  const isExpanded = mode === "expanded";
  const isPeek = mode === "peek";
  const height = isExpanded ? EXPANDED_H : isPeek ? PEEK_H : COLLAPSED_H;

  // Slight slant + lift on peek — reads like the folder is being pulled
  // halfway out of a filing drawer.
  const bodyTransform = isPeek ? "translateY(-6px) rotate(-1.1deg)" : "translateY(0) rotate(0deg)";
  const tabTransform = isPeek || isExpanded ? "translateY(-5px)" : "translateY(0)";

  return (
    <div
      className="relative"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{ zIndex: i + 1 }} // lower folders on top so their tabs are visible
    >
      {/* ── TAB (trapezoidal) — sits above the body, overlapping the folder above ── */}
      <button
        type="button"
        data-testid={`lab-folder-${p.idx}`}
        onClick={onClick}
        className="absolute z-20 font-mono-c text-[0.66rem] uppercase tracking-[0.24em]"
        style={{
          top: -TAB_H + 4, // overlap body top edge by 4px so no seam
          left: TAB_OFFSETS[i],
          width: TAB_W,
          height: TAB_H,
          background: p.tab,
          backgroundImage: FIBER,
          backgroundBlendMode: "soft-light",
          color: p.text,
          clipPath: TAB_CLIP,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          cursor: "pointer",
          fontWeight: 500,
          transform: tabTransform,
          transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
          boxShadow: "0 -2px 4px -2px rgba(0,0,0,0.10)",
        }}
      >
        <span className="flex h-full items-center justify-center whitespace-nowrap opacity-90">
          <span style={{ opacity: 0.6 }}>(&nbsp;</span>
          {p.name.toUpperCase()}
          <span style={{ opacity: 0.6 }}>&nbsp;)</span>
        </span>
      </button>

      {/* ── BODY ── */}
      <motion.div
        onClick={onClick}
        initial={false}
        animate={{ height }}
        transition={{ type: "spring", stiffness: 230, damping: 27, mass: 0.9 }}
        className="relative w-full overflow-hidden"
        style={{
          background: p.body,
          backgroundImage: FIBER,
          backgroundBlendMode: "soft-light",
          borderRadius: 14,
          transform: bodyTransform,
          transformOrigin: "50% 100%", // tilt from the bottom edge → the top lifts
          transition: "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease",
          boxShadow: isExpanded
            ? "0 26px 46px -22px rgba(0,0,0,0.28), 0 10px 18px -10px rgba(0,0,0,0.16)"
            : isPeek
              ? "0 20px 34px -18px rgba(0,0,0,0.24), 0 8px 14px -8px rgba(0,0,0,0.14)"
              : "0 8px 16px -12px rgba(0,0,0,0.18), 0 2px 4px -2px rgba(0,0,0,0.08)",
          cursor: "pointer",
        }}
      >
        {/* Top fold shadow (where tab meets body) */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-5"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, transparent 100%)" }}
        />

        {/* Row 1 — always visible: name + status */}
        <div
          className="flex items-center justify-between px-9"
          style={{ height: COLLAPSED_H }}
        >
          <h3
            className="font-display text-2xl font-light tracking-[-0.01em] md:text-[1.65rem]"
            style={{
              color: p.text,
              textDecoration: p.status === "ARCHIVED" ? "line-through" : "none",
              textDecorationColor: p.hairline,
              textDecorationThickness: "1.5px",
              textUnderlineOffset: "6px",
            }}
          >
            {p.name}
          </h3>
          <span
            className="hidden font-mono-c text-[0.6rem] uppercase tracking-[0.2em] md:inline"
            style={{
              color: p.softText,
              opacity: isExpanded ? 0 : 1,
              transition: "opacity 0.25s",
            }}
          >
            {p.status}
          </span>
        </div>

        {/* PEEK preview — description only, shown in peek state (not expanded) */}
        <AnimatePresence>
          {isPeek && !isExpanded && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 2 }}
              transition={{ duration: 0.28, ease: EASE, delay: 0.08 }}
              className="absolute inset-x-0 px-9"
              style={{ top: COLLAPSED_H }}
            >
              <p
                className="max-w-[440px] text-[0.85rem] leading-[1.5]"
                style={{ color: p.softText }}
              >
                {p.line}
              </p>
              <div
                className="absolute right-9 top-1 font-mono-c text-[0.56rem] uppercase tracking-[0.2em]"
                style={{ color: p.subtleText }}
              >
                click to open
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* EXPANDED content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.3, ease: EASE, delay: 0.1 }}
              className="absolute inset-x-0 px-9 pb-7"
              style={{ top: COLLAPSED_H, bottom: 0 }}
            >
              {/* Category — small, grey, top-right */}
              <div
                className="absolute right-9 top-2 font-mono-c text-[0.58rem] uppercase tracking-[0.2em]"
                style={{ color: p.subtleText }}
              >
                {p.category}
              </div>

              {/* Description — LEFT, constrained */}
              <p
                className="max-w-[440px] pt-3 text-[0.98rem] leading-relaxed"
                style={{ color: p.softText }}
              >
                {p.line}
              </p>

              {/* Stage — BOTTOM CENTRE */}
              <div className="absolute inset-x-0 bottom-12 flex justify-center">
                <StageIndicator p={p} />
              </div>

              {/* v1.2 / IN THE LAB — BOTTOM RIGHT */}
              <div className="absolute bottom-7 right-9 text-right">
                <div
                  className="font-mono-c text-[0.62rem] uppercase tracking-[0.18em]"
                  style={{ color: p.softText }}
                >
                  {p.meta}
                </div>
                <div
                  className="mt-0.5 font-mono-c text-[0.56rem] uppercase tracking-[0.22em]"
                  style={{ color: p.subtleText }}
                >
                  In the lab
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

/* ────────────────────── Page ────────────────────── */

export default function LoopLab() {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(null);

  const getMode = (i) => {
    if (expandedIdx === i) return "expanded";
    if (hoverIdx === i && expandedIdx === null) return "peek";
    return "collapsed";
  };

  const handleEnter = (i) => setHoverIdx(i);
  const handleLeave = () => setHoverIdx(null);
  const handleClick = (i) =>
    setExpandedIdx((prev) => (prev === i ? null : i));

  return (
    <div className="grain relative bg-page">
      <Nav />
      <main>
        {/* ============ Hero ============ */}
        <Section className="pb-24 pt-40 md:pb-28 md:pt-48">
          <Eyebrow>R &amp; D</Eyebrow>
          <div className="mt-10 grid grid-cols-1 items-start gap-12 md:mt-14 md:grid-cols-[1.15fr_0.85fr] md:gap-16 lg:gap-24">
            <div>
              <h1 className="font-display font-light leading-[1.02] tracking-[-0.02em] text-content text-[3rem] sm:text-6xl md:text-7xl lg:text-[6rem]">
                <MaskedLines
                  lines={[[
                    { t: "Loop", em: false, italic: true },
                    { t: " Lab.", em: false },
                  ]]}
                  stagger={0.1}
                  delay={0.08}
                  trigger="mount"
                />
              </h1>
              <FadeUp delay={0.28}>
                <p className="mt-8 max-w-md font-display text-xl font-light leading-[1.32] tracking-[-0.005em] text-mutedc md:text-2xl">
                  Half built. <span className="italic text-content">Fully interesting.</span>
                </p>
              </FadeUp>
            </div>

            <FadeUp delay={0.24} className="md:pt-3 lg:pt-4">
              <p className="max-w-md text-base leading-relaxed text-content md:text-lg">
                Loop Lab is our internal R&amp;D. We take a problem, build the smallest possible version of a solution, and see what breaks.
              </p>
            </FadeUp>
          </div>
        </Section>

        {/* ============ Current Shelf ============ */}
        <Section className="border-t border-line pb-40 pt-20 md:pt-28">
          <FadeUp>
            <p className="mx-auto max-w-3xl text-center font-display text-2xl font-light leading-[1.28] tracking-[-0.01em] text-content md:text-[1.85rem]">
              Some are experiments. Some become products.{" "}
              <span className="text-mutedc">All of them teach us something.</span>
            </p>
          </FadeUp>
          <div className="mt-10 flex justify-center md:mt-14">
            <Eyebrow>Current shelf</Eyebrow>
          </div>

          {/* Folder stack — flush, tabs staggered on x-axis */}
          <div className="mt-16 md:mt-20">
            <div className="relative mx-auto" style={{ maxWidth: BAND_W }}>
              <div className="space-y-0">
                {LAB_PROJECTS.map((p, i) => (
                  <Folder
                    key={p.idx}
                    p={p}
                    i={i}
                    mode={getMode(i)}
                    onEnter={() => handleEnter(i)}
                    onLeave={handleLeave}
                    onClick={() => handleClick(i)}
                  />
                ))}
              </div>
            </div>

            <div
              className="mx-auto mt-16 flex flex-wrap items-baseline justify-end gap-6 border-t border-line pt-6 font-mono-c text-[0.6rem] uppercase tracking-[0.2em] text-mutedc"
              style={{ maxWidth: BAND_W }}
            >
              <span>Hover to peek · click to open</span>
            </div>
          </div>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
